import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import logoImage from '../assets/images/bgLogo.png';
import armImage from '../assets/images/armlogo.png';
import '../styles/armsform.css';

const ArmsLicenseList = () => {
  const [gdsrEntries, setGdsrEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const response = await axios.post('https://chaman.gob.pk/eac_backend/arms.php', {
          action: 'get_records',
        });

        if (Array.isArray(response.data)) {
          setGdsrEntries(response.data);
        } else {
          setGdsrEntries([]);
          console.error('Expected array but got:', response.data);
        }
      } catch (err) {
        setError('Failed to fetch Arms entries');
      } finally {
        setLoading(false);
      }
    };

    fetchEntries();
  }, []);

  const handlePrint = async (entry) => {
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });
  
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
  
    // Draw the first box (1" width, 1.25" height)
    const boxX = 35; // Distance from the left side
    const boxY = 45; // Distance from the top
    const boxWidth = 30; // 1 inch = 25.4 mm
    const boxHeight = 35.75; // 1.25 inches = 31.75 mm
    doc.rect(boxX, boxY, boxWidth, boxHeight);
  
    // Draw the second box (1" width, 2.25" height) below the first one
    const secondBoxY = boxY + boxHeight + 5; // 5mm space between the boxes
    const secondBoxHeight = 57.15; // 2.25 inches = 57.15 mm
    doc.rect(boxX, secondBoxY, boxWidth, secondBoxHeight);
  
    doc.setGState(new doc.GState({ opacity: 0.2 }));
  
    const maxWidth = 180;
    const maxHeight = 130;
  
    const originalWidth = 330;
    const originalHeight = 280;
    const aspectRatio = originalWidth / originalHeight;
  
    let bgLogoWidth = maxWidth;
    let bgLogoHeight = maxWidth / aspectRatio;
  
    if (bgLogoHeight > maxHeight) {
      bgLogoHeight = maxHeight;
      bgLogoWidth = maxHeight * aspectRatio;
    }
  
    const centerX = (pageWidth - bgLogoWidth) / 2;
    const centerY = (pageHeight - bgLogoHeight) / 2;
    doc.addImage(logoImage, 'PNG', centerX, centerY, bgLogoWidth, bgLogoHeight);
  
    doc.setGState(new doc.GState({ opacity: 1 }));
  
    const logoWidth = 15;
    const logoHeight = 20;
    const logoX = (pageWidth - logoWidth) / 2;
    doc.addImage(armImage, 'PNG', logoX, 10, logoWidth, logoHeight);
  
    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('FORM XVI', pageWidth / 2, 38, { align: 'center' });
    const underlineWidth = doc.getTextWidth('FORM XVI');
    doc.line(
      (pageWidth - underlineWidth) / 2,
      39,
      (pageWidth + underlineWidth) / 2,
      39
    );
  
    doc.setFontSize(10);
    doc.text('POSSESSION OF THE ARMS AND AMMUNITION FOR', pageWidth / 2, 45, { align: 'center' });
    doc.text('GOING PURPOSE OF PROTECTION AND DISPLAY', pageWidth / 2, 50, { align: 'center' });
  
    const tableX = 70;
    const tableY = 52;
  
    const columnWidths = [
      0.85 * 72,
      0.45 * 72,
      0.50 * 72,
      0.35 * 72,
      0.35 * 72,
      0.35 * 72,
    ];
  
    const headers = [
      'NAME DESCRIPTION & RESIDENCE OF THE LICENSE AND AGENT',
      'QUANTITY & DESCRIPTION OF EACH KIND OF',
      'BRIEF DESCRIPTION OF ARMS',
      'NAME OF RETAINER',
      'VALIDITY OF THE LICENSE',
      'DATE ON WHICH THE LICENSE EXPIRES',
    ];
  
    const headerHeights = headers.map((header, index) => {
      const columnWidth = columnWidths[index];
      const headerLines = doc.splitTextToSize(header, columnWidth - 4);
      return headerLines.length * 7;
    });
  
    const maxHeaderHeight = Math.max(...headerHeights);
  
    doc.setFontSize(9);
    let currentX = tableX;
    headers.forEach((header, index) => {
      const columnWidth = columnWidths[index];
      const headerLines = doc.splitTextToSize(header, columnWidth - 4);
      doc.rect(currentX, tableY, columnWidth, maxHeaderHeight);
      headerLines.forEach((line, lineIndex) => {
        doc.text(line, currentX + columnWidth / 2, tableY + 6 + lineIndex * 6, { align: 'center' });
      });
      currentX += columnWidths[index];
    });
  
    let currentY = tableY + maxHeaderHeight + 2;
  
    // Prepare the row for the selected entry with each column having its own line
    const row = [
      doc.splitTextToSize(`${entry.name} 'S/O' ${entry.father_name} 'R/O' ${entry.address}`, columnWidths[0] - 4), // Name, father name, address
      doc.splitTextToSize(`${entry.weapon} 'No' ${entry.no} 'Bore' ${entry.bore}`, columnWidths[1] - 4), // Weapon, no, bore
      doc.splitTextToSize(`${entry.cartridge_s} Cartridges a time ${entry.cartridge_a} Cartridges Annually`, columnWidths[2] - 4), // Cartridges info
      doc.splitTextToSize(entry.gender || '', columnWidths[3] - 4), // Gender
      doc.splitTextToSize(entry.validity || '', columnWidths[4] - 4), // Validity
      doc.splitTextToSize(entry.expiry || '', columnWidths[5] - 4), // Expiry date
    ];
  
    currentX = tableX;
    const rowHeights = row.map((cell, index) => {
      return cell.length * 6; // Adjust line height as needed
    });
  
    const maxRowHeight = Math.max(...rowHeights);
  
    // Draw each cell in the row
    row.forEach((cellLines, index) => {
      const columnWidth = columnWidths[index];
      cellLines.forEach((line, lineIndex) => {
        doc.text(line, currentX + 2, currentY + 6 + lineIndex * 6, { align: 'left' });
      });
      currentX += columnWidth;
    });
  
    currentY += maxRowHeight;
  
    window.open(doc.output('bloburl'));
  };
  
  
  
  

  const handleDelete = async (token_id) => {
    if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
      try {
        const response = await axios.post('https://chaman.gob.pk/eac_backend/arms.php', {
          action: 'delete',
          token_id,
        });
        if (response.data.status === 'success') {
          setGdsrEntries((prevEntries) => prevEntries.filter((entry) => entry.token_id !== token_id));
          alert('Entry deleted successfully.');
        } else {
          alert('Error deleting entry: ' + response.data.message);
        }
      } catch (error) {
        alert('Error deleting entry');
      }
    }
  };

  if (loading) return <div>Loading entries...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="arms-list">
      <div className="listsearch">
        <input
          id="lstsea"
          type="text"
          placeholder="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <table>
        <thead>
          <tr>
            <th>Token ID</th>
            <th>License No</th>
            <th>Date</th>
            <th>Name</th>
            <th>Father Name</th>
            <th>Profession</th>
            <th>Weapon</th>
            <th>Weapon Type</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {gdsrEntries
            .filter((entry) =>
              Object.values(entry).some((value) =>
                String(value).toLowerCase().includes(searchTerm.toLowerCase())
              )
            )
            .map((entry) => (
              <tr key={entry.token_id}>
                <td>{entry.token_id}</td>
                <td>{entry.license_no}</td>
                <td>{entry.date}</td>
                <td>{entry.name}</td>
                <td>{entry.father_name}</td>
                <td>{entry.profession}</td>
                <td>{entry.weapon}</td>
                <td>{entry.weapon_type}</td>
                <td>{entry.status}</td>
                <td>
                  <div className="listbuttons">
                    <button id="lstbtn" onClick={() => handlePrint(entry)} title="Print">
                      <FontAwesomeIcon icon={faPrint} />
                    </button>
                    {/* <button id="lstbtn" onClick={() => handleDelete(entry.token_id)} title="Delete">
                      <FontAwesomeIcon icon={faTrash} />
                    </button> */}
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
};

export default ArmsLicenseList;
